import { cn } from "@/lib/utils";
import type { CSSProperties } from "react";

interface MarkProps {
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

/**
 * VertaliMark — pure SVG. By default uses currentColor; pass a className
 * like `text-primary` to brand it.
 */
export function VertaliMark({ size = 28, className, style, title }: MarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <path d="M4.5 9 L16 25 L27.5 9" />
      <path d="M10.5 8 L16 15 L21.5 8" opacity="0.55" />
      <path d="M14 7 L16 9.5 L18 7" opacity="0.32" />
    </svg>
  );
}

interface LogoProps {
  /** Pixel height of the mark; wordmark scales with it. */
  size?: number;
  /** Hide the wordmark and only render the mark. */
  markOnly?: boolean;
  /** Opacity of the secondary "weather" word (relative to current text color). Default 0.55. */
  secondaryOpacity?: number;
  /**
   * Color story:
   *   "branded" (default) — mark renders in `--primary` with a soft halo;
   *                          wordmark inherits the surrounding text color.
   *   "monochrome"        — mark and wordmark both inherit the surrounding color.
   */
  variant?: "branded" | "monochrome";
  /** Tailwind classes for the outer span (controls wordmark color). */
  className?: string;
  /** ARIA label for screen readers. */
  label?: string;
}

/**
 * VertaliLogo — full lockup. Branded mark (primary) + clean wordmark (current
 * color). The wordmark is real text so it stays sharp at any size and matches
 * the surrounding font (Geist).
 */
export function VertaliLogo({
  size = 28,
  markOnly = false,
  secondaryOpacity = 0.55,
  variant = "branded",
  className,
  label = "Vertali Weather",
}: LogoProps) {
  const fontSize = Math.round(size * 0.78);
  const markClass =
    variant === "branded"
      ? "text-primary drop-shadow-[0_0_14px_var(--color-primary)]"
      : undefined;
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        "inline-flex items-center gap-2 select-none leading-none",
        className,
      )}
      style={{ height: size }}
    >
      <VertaliMark size={size} className={markClass} />
      {!markOnly && (
        <span
          aria-hidden
          className="font-black tracking-tight lowercase"
          style={{ fontSize: `${fontSize}px`, letterSpacing: "-0.01em" }}
        >
          vertali
          <span
            className="font-medium ml-[1px]"
            style={{ opacity: secondaryOpacity }}
          >
            weather
          </span>
        </span>
      )}
    </span>
  );
}
